// Google Maps travel time calculator
// Uses Directions REST API via CRA dev proxy (bypasses CORS)

import { cachedFetch } from './cacheUtils.js';

const GEOCODE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const getApiKey = () => {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) throw new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY');
  return key;
};

const getDirections = async (origin, destination) => {
  const apiKey = getApiKey();
  const params = new URLSearchParams({
    origin,
    destination,
    region: 'ca',
    key: apiKey,
  });

  const res = await fetch(`/api/google-directions?${params}`);
  if (!res.ok) {
    throw new Error(`Directions API HTTP error: ${res.status}`);
  }
  const data = await res.json();

  if (data.status === 'OK' && data.routes.length > 0) {
    const leg = data.routes[0].legs[0];
    return {
      durationSeconds: leg.duration.value,
      durationHours: parseFloat((leg.duration.value / 3600).toFixed(2)),
      distanceMeters: leg.distance.value,
      distanceKm: parseFloat((leg.distance.value / 1000).toFixed(1)),
      description: `Drive from ${leg.start_address.split(',')[0]} to ${leg.end_address.split(',')[0]}`,
    };
  } else {
    throw new Error(`Directions: ${data.status} — ${data.error_message || 'unknown error'}`);
  }
};

/**
 * Geocode an address string to lat/lng via Google Geocoding API.
 */
export const geocodeAddress = async (address) => {
  const apiKey = getApiKey();
  const params = new URLSearchParams({ address, region: 'ca', key: apiKey });
  const res = await fetch(`/api/google-geocode?${params}`);
  if (!res.ok) throw new Error(`Geocoding HTTP error: ${res.status}`);
  const data = await res.json();
  if (data.status === 'OK' && data.results.length > 0) {
    const { lat, lng } = data.results[0].geometry.location;
    return { lat, lng };
  }
  throw new Error(`Geocoding: ${data.status} — ${data.error_message || address}`);
};

/**
 * Reverse-geocode a device fix to a street address via the same Geocoding
 * endpoint (latlng instead of address). Returns the most specific formatted
 * address, or null when Google has nothing for those coordinates — callers
 * fall back to the raw coordinates, which Maps still routes to.
 */
export const reverseGeocode = async (lat, lng) => {
  const apiKey = getApiKey();
  const params = new URLSearchParams({ latlng: `${lat},${lng}`, region: 'ca', key: apiKey });
  const res = await fetch(`/api/google-geocode?${params}`);
  if (!res.ok) throw new Error(`Reverse geocoding HTTP error: ${res.status}`);
  const data = await res.json();
  if (data.status === 'OK' && data.results.length > 0) {
    return data.results[0].formatted_address;
  }
  if (data.status === 'ZERO_RESULTS') return null;
  throw new Error(`Reverse geocoding: ${data.status} — ${data.error_message || ''}`);
};

/**
 * Cached + in-flight-deduped wrapper around geocodeAddress.
 * Returns null on failure rather than throwing (callers usually want to fall through).
 */
export const geocodeCached = async (address) => {
  if (!address || !address.trim()) return null;
  const norm = address.toLowerCase().trim();
  return cachedFetch(`geocode:${norm}`, GEOCODE_TTL_MS, async () => {
    try {
      const { lat, lng } = await geocodeAddress(address);
      return { lat, lng };
    } catch (err) {
      console.warn('Geocoding failed:', address, err.message);
      return null;
    }
  });
};

/**
 * Call Directions API with optimizeWaypoints to get the optimal visit order.
 * Returns the reordered waypoint indices (0-based).
 */
export const getOptimizedOrder = async (origin, destination, waypoints) => {
  if (waypoints.length < 2) return waypoints.map((_, i) => i);

  const apiKey = getApiKey();
  const waypointStr = 'optimize:true|' + waypoints.join('|');
  const params = new URLSearchParams({
    origin,
    destination,
    waypoints: waypointStr,
    region: 'ca',
    key: apiKey,
  });

  const res = await fetch(`/api/google-directions?${params}`);
  if (!res.ok) throw new Error(`Directions API HTTP error: ${res.status}`);
  const data = await res.json();

  if (data.status === 'OK' && data.routes.length > 0) {
    return data.routes[0].waypoint_order;
  }
  throw new Error(`Directions optimization: ${data.status} — ${data.error_message || 'unknown'}`);
};

/**
 * Get road distances from one origin to multiple destinations via Distance Matrix API.
 * Returns array of { distanceKm, durationHours } in the same order as destinations.
 */
export const getDistanceMatrix = async (origin, destinations) => {
  const apiKey = getApiKey();
  const params = new URLSearchParams({
    origins: origin,
    destinations: destinations.join('|'),
    region: 'ca',
    key: apiKey,
  });

  const res = await fetch(`/api/google-distancematrix?${params}`);
  if (!res.ok) throw new Error(`Distance Matrix HTTP error: ${res.status}`);
  const data = await res.json();

  if (data.status !== 'OK') {
    throw new Error(`Distance Matrix: ${data.status} — ${data.error_message || 'unknown'}`);
  }

  return data.rows[0].elements.map((el) => {
    if (el.status !== 'OK') return { distanceKm: null, durationHours: null };
    return {
      distanceKm: parseFloat((el.distance.value / 1000).toFixed(1)),
      durationHours: parseFloat((el.duration.value / 3600).toFixed(2)),
    };
  });
};

/**
 * Get leg-by-leg travel times for an ordered sequence of stops.
 * Returns array of { durationHours, distanceKm } for each leg (N+1 legs for N waypoints).
 */
export const getRouteLegTimes = async (origin, destination, waypoints) => {
  const apiKey = getApiKey();
  const params = new URLSearchParams({
    origin,
    destination,
    region: 'ca',
    key: apiKey,
  });
  if (waypoints.length > 0) {
    params.set('waypoints', waypoints.join('|'));
  }

  const res = await fetch(`/api/google-directions?${params}`);
  if (!res.ok) throw new Error(`Directions API HTTP error: ${res.status}`);
  const data = await res.json();

  if (data.status === 'OK' && data.routes.length > 0) {
    return data.routes[0].legs.map((leg) => ({
      durationHours: parseFloat((leg.duration.value / 3600).toFixed(2)),
      distanceKm: parseFloat((leg.distance.value / 1000).toFixed(1)),
    }));
  }
  throw new Error(`Directions: ${data.status} — ${data.error_message || 'unknown'}`);
};

/**
 * Compute travel hours/distance/description for a user-added Transit row.
 * Used by TransitFormModal on create/edit and by RouteDetailPage recalc.
 *
 * @param {Object} params
 * @param {string} params.priorEndpoint - address the driver was at before this Transit
 *   (prior item destination, hotel, or home base).
 * @param {Object} params.transit - { travel_destination, travel_origin_override }
 * @returns {Object|null} { travel_hours, travel_distance_km, travel_description } or null if inputs insufficient
 */
export const computeTransitTravel = async ({ priorEndpoint, transit }) => {
  const override = transit.travel_origin_override && transit.travel_origin_override.trim();
  const origin = override || priorEndpoint;
  const destination = transit.travel_destination;
  if (!origin || !destination) return null;

  const result = await getDirections(origin, destination);
  return {
    travel_hours: result.durationHours,
    travel_distance_km: result.distanceKm,
    travel_description: `${origin.split(',')[0].trim()} → ${destination.split(',')[0].trim()}`,
  };
};

const HOTEL_MINUTES = 25;
const HOTEL_HOURS = parseFloat((HOTEL_MINUTES / 60).toFixed(2));

/**
 * Calculate travel times for given days of a route.
 *
 * Rules (simplified):
 *   Start of day:  Route's first day → from home base.  Otherwise → from hotel (25 min).
 *   End of day:    Route's last day → to home base.  Otherwise → to hotel (25 min).
 *   All toggleable via dayOverrides.
 *
 * @param {Array} items - route_items for the route (JOB rows)
 * @param {Array} dayNumbers - which days to calculate
 * @param {Object} options
 * @param {string} options.homeBase - applicator home address
 * @param {Object} options.dayOverrides - persisted overrides per day:
 *   { [dayNum]: { startType, startOrigin, endType } }
 *   startType/endType: 'home' | 'hotel'
 *   startOrigin: custom address string (overrides homeBase for that day)
 * @param {Function} options.onProgress - progress callback
 */
export const calculateTravelForDays = async (items, dayNumbers, options = {}) => {
  const { homeBase, dayOverrides = {}, onProgress } = options;
  const homeCity = homeBase ? homeBase.split(',')[0].trim() : 'Home';
  let progressCount = 0;
  const totalJobs = items.filter(i => dayNumbers.includes(i.day_number) && i.row_type === 'JOB').length;
  const report = (desc) => {
    progressCount++;
    if (onProgress) onProgress(progressCount, totalJobs + dayNumbers.length, desc);
  };

  // Determine first/last route day (for start/end-of-day home base default)
  const allDayNumbers = [...new Set(items.filter(i => i.row_type === 'JOB').map(i => i.day_number))];
  const firstRouteDay = allDayNumbers.length > 0 ? Math.min(...allDayNumbers) : null;
  const lastRouteDay = allDayNumbers.length > 0 ? Math.max(...allDayNumbers) : null;

  const travelRows = [];

  for (const dayNum of dayNumbers) {
    const dayJobs = items
      .filter((i) => i.day_number === dayNum && i.row_type === 'JOB')
      .sort((a, b) => a.sort_order - b.sort_order);

    if (dayJobs.length === 0) continue;

    const firstJob = dayJobs[0];
    const lastJob = dayJobs[dayJobs.length - 1];
    const overrides = dayOverrides[dayNum] || {};

    // ─── Start of day ─────────────────────────────
    // Default: route's first day = home, otherwise hotel. Override takes priority.
    const isFirstDay = dayNum === firstRouteDay;
    const startType = overrides.startType || (isFirstDay ? 'home' : 'hotel');
    const startOrigin = overrides.startOrigin || homeBase;

    if (startType === 'home' && startOrigin) {
      const originCity = startOrigin.split(',')[0].trim();
      const originLabel = startOrigin === homeBase ? `Home Base (${homeCity})` : originCity;

      if (!firstJob.location) {
        travelRows.push({
          day_number: dayNum,
          sort_order_after: firstJob.sort_order - 1,
          travel_description: `${originLabel} → ${firstJob.company || 'first job'} — no address, 30 min est.`,
          travel_hours: 0.5, travel_distance_km: null,
          travel_type: 'start_of_day_home',
          travel_origin_override: overrides.startOrigin || null,
        });
      } else {
        try {
          report(`Day ${dayNum}: ${originLabel} → ${firstJob.company || firstJob.location}`);
          const result = await getDirections(startOrigin, firstJob.location);
          const destName = result.description.split(' to ')[1] || firstJob.company || firstJob.location;
          travelRows.push({
            day_number: dayNum,
            sort_order_after: firstJob.sort_order - 1,
            travel_description: `${originLabel} → ${destName}`,
            travel_hours: result.durationHours,
            travel_distance_km: result.distanceKm,
            travel_type: 'start_of_day_home',
            travel_origin_override: overrides.startOrigin || null,
          });
        } catch (err) {
          console.warn(`Start-of-day travel estimate used (30 min):`, err.message);
          travelRows.push({
            day_number: dayNum,
            sort_order_after: firstJob.sort_order - 1,
            travel_description: `${originLabel} → ${firstJob.company || 'first job'} (~30 min est.)`,
            travel_hours: 0.5, travel_distance_km: null,
            travel_type: 'start_of_day_home',
            travel_origin_override: overrides.startOrigin || null,
          });
        }
      }
    } else {
      // Hotel start
      travelRows.push({
        day_number: dayNum,
        sort_order_after: firstJob.sort_order - 1,
        travel_description: `Hotel → ${firstJob.company || firstJob.location || 'first job'} (~${HOTEL_MINUTES} min)`,
        travel_hours: HOTEL_HOURS,
        travel_distance_km: null,
        travel_type: 'start_of_day_hotel',
        travel_origin_override: null,
      });
    }

    // ─── Inter-job travel ────────────────────────────
    for (let i = 0; i < dayJobs.length - 1; i++) {
      const from = dayJobs[i];
      const to = dayJobs[i + 1];
      if (!from.location || !to.location) {
        travelRows.push({
          day_number: dayNum,
          sort_order_after: from.sort_order,
          travel_description: `${from.company || 'prev'} → ${to.company || 'next'} — no address, 30 min est.`,
          travel_hours: 0.5, travel_distance_km: null,
          travel_type: 'inter_job',
        });
        continue;
      }
      try {
        report(`Day ${dayNum}: ${from.company || 'prev'} → ${to.company || 'next'}`);
        const result = await getDirections(from.location, to.location);
        travelRows.push({
          day_number: dayNum,
          sort_order_after: from.sort_order,
          travel_description: result.description,
          travel_hours: result.durationHours,
          travel_distance_km: result.distanceKm,
          travel_type: 'inter_job',
        });
      } catch (err) {
        console.warn(`Inter-job travel estimate used (30 min):`, err.message);
        travelRows.push({
          day_number: dayNum,
          sort_order_after: from.sort_order,
          travel_description: `${from.company || 'prev'} → ${to.company || 'next'} (~30 min est.)`,
          travel_hours: 0.5, travel_distance_km: null,
          travel_type: 'inter_job',
        });
      }
    }

    // ─── End of day ─────────────────────────────────
    // Default: route's last day = home, otherwise hotel. Override takes priority.
    const isLastDay = dayNum === lastRouteDay;
    const endType = overrides.endType || (isLastDay ? 'home' : 'hotel');

    if (endType === 'home') {
      const endDest = overrides.endDestination || homeBase;
      const endCity = endDest ? endDest.split(',')[0].trim() : 'Home';
      const endLabel = (!overrides.endDestination || overrides.endDestination === homeBase)
        ? `Home Base (${homeCity})` : endCity;

      if (!endDest) {
        travelRows.push({
          day_number: dayNum,
          sort_order_after: lastJob.sort_order,
          travel_description: `${lastJob.company || 'last job'} → Home Base (set address)`,
          travel_hours: 0.5, travel_distance_km: null,
          travel_type: 'end_of_day_home',
          travel_origin_override: null,
        });
      } else if (!lastJob.location) {
        travelRows.push({
          day_number: dayNum,
          sort_order_after: lastJob.sort_order,
          travel_description: `${lastJob.company || 'last job'} → ${endLabel} — no address, 30 min est.`,
          travel_hours: 0.5, travel_distance_km: null,
          travel_type: 'end_of_day_home',
          travel_origin_override: overrides.endDestination || null,
        });
      } else {
        try {
          report(`Day ${dayNum}: ${lastJob.company || 'last job'} → ${endLabel}`);
          const result = await getDirections(lastJob.location, endDest);
          const originName = result.description.split(' from ')[1]?.split(' to ')[0] || lastJob.company || lastJob.location;
          travelRows.push({
            day_number: dayNum,
            sort_order_after: lastJob.sort_order,
            travel_description: `${originName} → ${endLabel}`,
            travel_hours: result.durationHours,
            travel_distance_km: result.distanceKm,
            travel_type: 'end_of_day_home',
            travel_origin_override: overrides.endDestination || null,
          });
        } catch (err) {
          console.warn(`End-of-day travel estimate used (30 min):`, err.message);
          travelRows.push({
            day_number: dayNum,
            sort_order_after: lastJob.sort_order,
            travel_description: `${lastJob.company || 'last job'} → ${endLabel} (~30 min est.)`,
            travel_hours: 0.5, travel_distance_km: null,
            travel_type: 'end_of_day_home',
            travel_origin_override: overrides.endDestination || null,
          });
        }
      }
    } else {
      // Hotel end
      travelRows.push({
        day_number: dayNum,
        sort_order_after: lastJob.sort_order,
        travel_description: `${lastJob.company || lastJob.location || 'last job'} → Hotel (~${HOTEL_MINUTES} min)`,
        travel_hours: HOTEL_HOURS,
        travel_distance_km: null,
        travel_type: 'end_of_day_hotel',
      });
    }
  }

  return travelRows;
};
